import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TEMPLATE_TYPE } from '../../../../constants/messageTemplateKeys';
import {
  selectTriggerValidationRowErrors,
  triggerValidationCleared,
  validateTriggerAssignments,
} from '../../../../store/templatesSlice';

// Build the payload from complete rows only, keeping each sent entry's original row
// index so backend per-index errors can be mapped back. A row is complete once its
// send condition is set and its day is filled — except for rows where Days is optional
// (dayOptionalByRow[index]), where a send condition alone is enough and an empty day is
// sent as null.
function toValidationTriggers(triggerId, sendConditions, dayOptionalByRow = []) {
  const id = Number(String(triggerId ?? '').trim());
  if (!Number.isFinite(id) || id <= 0) return { triggers: [], rowIndices: [] };

  const triggers = [];
  const rowIndices = [];
  (Array.isArray(sendConditions) ? sendConditions : []).forEach((row, index) => {
    const hasSendCondition = String(row?.sendCondition ?? '').trim().length > 0;
    const dayRaw = String(row?.day ?? '').trim();
    if (hasSendCondition && (dayRaw.length > 0 || dayOptionalByRow[index])) {
      triggers.push({
        trigger: id,
        send_condition: String(row.sendCondition),
        day: dayRaw.length > 0 ? Number(dayRaw) : null,
      });
      rowIndices.push(index);
    }
  });
  return { triggers, rowIndices };
}

export function useTriggerValidation({
  type,
  triggerId,
  sendConditions,
  templateId = null,
  dayOptionalByRow = [],
}) {
  const dispatch = useDispatch();
  const rowErrors = useSelector(selectTriggerValidationRowErrors);

  const { triggers, rowIndices } = toValidationTriggers(
    triggerId,
    sendConditions,
    dayOptionalByRow,
  );
  const triggersKey = JSON.stringify(triggers);

  // A field blur bumps this tick to re-evaluate the current state.
  const [validateTick, setValidateTick] = useState(0);
  const validateNow = useCallback(() => setValidateTick((tick) => tick + 1), []);

  // Re-evaluate on discrete changes (trigger / send-condition pick) or on a blur
  // tick — not on day typing. The actual request only fires when the set of fully
  // filled rows changed, so adding an empty row or picking a condition without a
  // day sends nothing.
  const conditionKey = JSON.stringify({
    triggerId,
    conditions: (Array.isArray(sendConditions) ? sendConditions : []).map(
      (row) => row?.sendCondition ?? '',
    ),
  });

  const lastValidatedRef = useRef('');

  useEffect(() => {
    if (type !== TEMPLATE_TYPE.TRIGGER || triggers.length === 0) {
      if (lastValidatedRef.current !== '') {
        dispatch(triggerValidationCleared());
        lastValidatedRef.current = '';
      }
      return;
    }
    if (triggersKey === lastValidatedRef.current) return;
    lastValidatedRef.current = triggersKey;
    dispatch(validateTriggerAssignments({ triggers, templateId, rowIndices }));
    // Fires on condition/trigger changes and blur ticks; the triggersKey guard skips
    // re-sending when the complete-row payload is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionKey, validateTick, dispatch]);

  useEffect(() => () => dispatch(triggerValidationCleared()), [dispatch]);

  return { rowErrors, validateNow };
}
